// The tool an agent records what a check of a merged pull request found with,
// such as the dead code it left behind (the dead-code skill). cubic checks the
// merge and the commit searched, then saves each finding where the agent put it
// (`../report-findings-call.ts`). No Node imports: the session permissions read
// the name.

import { z } from 'zod';

import type { SessionToolDefinition } from '.';

import { DISMISS_TRIAGE_STATUSES } from '@/lib/repo-scan/triage-status-labels';

export const REPORT_FINDINGS_TOOL = 'report_findings';

/** What a check looks for. Each kind has a skill that says how; duplicate code could be next. */
export const FINDING_KINDS = ['dead_code'] as const;

export type FindingKind = (typeof FINDING_KINDS)[number];

/** At most this many findings or `not_dead` items per call. */
export const MAX_REPORTED_FINDINGS = 20;

const NAME_MAX_LENGTH = 200;
const PATH_MAX_LENGTH = 1000;
const REASON_MAX_LENGTH = 1000;
const EVIDENCE_MAX_LENGTH = 4000;
const LABEL_MAX_LENGTH = 120;
const FINDING_TITLE_MAX_LENGTH = 160;
const CHANGE_MAX_LENGTH = 1000;
const SUMMARY_MAX_LENGTH = 600;
const TITLE_MAX_LENGTH = 80;
const MAX_FINDING_LINE = 2_147_483_647;

const commitSchema = z.string().regex(/^[0-9a-f]{40}$/i);

const reportedFindingSchema = z.object({
  name: z.string().trim().min(1).max(NAME_MAX_LENGTH),
  path: z.string().trim().min(1).max(PATH_MAX_LENGTH),
  line: z.number().int().positive().max(MAX_FINDING_LINE),
  reason: z.string().trim().min(1).max(REASON_MAX_LENGTH),
  evidence: z.string().trim().min(1).max(EVIDENCE_MAX_LENGTH),
  // Optional: the agent may leave them out, and a session keeps the tool definition it started
  // with; readers fall back to the name.
  label: z.string().trim().min(1).max(LABEL_MAX_LENGTH).optional(),
  change: z.string().trim().min(1).max(CHANGE_MAX_LENGTH).optional(),
  // The finding's title in the codebase scan, on the fix page and in Slack. Optional for the same
  // reason; without it cubic titles the finding after its name.
  title: z.string().trim().min(1).max(FINDING_TITLE_MAX_LENGTH).optional(),
});

export const reportFindingsInputSchema = z.object({
  pull_request: z.number().int().positive(),
  kind: z.enum(FINDING_KINDS),
  commit: commitSchema.optional(),
  findings: z.array(reportedFindingSchema).max(MAX_REPORTED_FINDINGS).optional(),
  // Optional, like `label`: what the author reads first, and the session's title.
  summary: z.string().trim().min(1).max(SUMMARY_MAX_LENGTH).optional(),
  title: z.string().trim().min(1).max(TITLE_MAX_LENGTH).optional(),
  not_dead: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(NAME_MAX_LENGTH),
        path: z.string().trim().min(1).max(PATH_MAX_LENGTH).optional(),
        status: z.enum(DISMISS_TRIAGE_STATUSES),
        reason: z.string().trim().min(1).max(REASON_MAX_LENGTH),
      }),
    )
    .max(MAX_REPORTED_FINDINGS)
    .optional(),
});

export type ReportFindingsInput = z.infer<typeof reportFindingsInputSchema>;

/** cubic's accepted result: the findings as it saved them, not an input supplied by the model. */
export const reportFindingsResultSchema = z.object({
  summary: z.string(),
  findings: z
    .array(reportedFindingSchema.pick({ name: true, path: true, line: true }))
    .max(MAX_REPORTED_FINDINGS),
});

export type ReportFindingsResult = z.infer<typeof reportFindingsResultSchema>;

export const REPORT_FINDINGS_TOOL_DEFINITION = {
  name: REPORT_FINDINGS_TOOL,
  description: [
    'Record what you found when you checked a merged pull request, such as code it left unused, so cubic can track each finding, link the pull request that removes it, and close it when that merges. Load the skill for the kind of check first; it says what to look for and how to prove it.',
    'After you have checked, call it with at most 20 findings you proved and the commit you searched. Repeat calls with the same pull request and searched commit until all findings are recorded; each call adds findings you did not record before. Give `summary` and `title` with every call that records findings, each covering everything recorded for the pull request so far. When you found nothing, call it with an empty `findings` list, so cubic knows the check finished.',
    'When someone says a finding is not what you reported, call it with that finding under `not_dead`, with the status that fits and their reason, and without `findings`: record findings and set them aside in separate calls. Each call accepts at most 20 `not_dead` items; repeat calls for more.',
    'cubic checks the pull request and the commit you searched, then saves each finding where you put it, so give the line its definition starts on. When it refuses a call, nothing is saved. Retry only when it identifies a correctable input, fixing it first. If it says the check is unavailable or not supported, or tells you to tell the user, report what you found and the refusal to the user instead of retrying.',
  ].join('\n\n'),
  input: {
    type: 'object',
    additionalProperties: false,
    required: ['pull_request', 'kind'],
    properties: {
      pull_request: {
        type: 'integer',
        minimum: 1,
        description: "The merged pull request's number, in this session's repository.",
      },
      kind: {
        type: 'string',
        enum: FINDING_KINDS,
        description: 'What you checked for: `dead_code`, code the pull request left unused.',
      },
      commit: {
        type: 'string',
        pattern: '^[0-9a-fA-F]{40}$',
        description:
          "The full SHA of the default-branch commit you searched. Required with `findings`: each finding's line is in this commit.",
      },
      findings: {
        type: 'array',
        maxItems: MAX_REPORTED_FINDINGS,
        description:
          'A batch of at most 20 findings you proved. Repeat calls with the same pull request and searched commit until all findings are recorded, or send an empty list when you found nothing. Leave it out when you pass `not_dead`.',
        items: {
          type: 'object',
          additionalProperties: false,
          required: ['name', 'path', 'line', 'reason', 'evidence'],
          properties: {
            name: {
              type: 'string',
              minLength: 1,
              maxLength: NAME_MAX_LENGTH,
              description:
                'The unused code\'s name as it is written there, such as "formatLegacyDate". cubic identifies the finding by it; the author reads `label`.',
            },
            path: {
              type: 'string',
              minLength: 1,
              maxLength: PATH_MAX_LENGTH,
              description: 'Its file, relative to the repository root.',
            },
            line: {
              type: 'integer',
              minimum: 1,
              maximum: MAX_FINDING_LINE,
              description: 'The line its definition starts on in `commit`.',
            },
            label: {
              type: 'string',
              minLength: 1,
              maxLength: LABEL_MAX_LENGTH,
              description:
                'A few plain words naming what it is for someone skimming, such as "The old date formatter". The author sees it in place of the name, linked to the code.',
            },
            reason: {
              type: 'string',
              minLength: 1,
              maxLength: REASON_MAX_LENGTH,
              description:
                'One short sentence for the author, in plain words: why this item is no longer needed. Leave out what `summary` already says.',
            },
            evidence: {
              type: 'string',
              minLength: 1,
              maxLength: EVIDENCE_MAX_LENGTH,
              description:
                "How you know nothing uses it, in one short plain sentence the author can take in at a glance: where you looked and what you found, in everyday terms. The details of your search stay in the session's work log.",
            },
            change: {
              type: 'string',
              minLength: 1,
              maxLength: CHANGE_MAX_LENGTH,
              description:
                'What removing it takes away, in one short plain sentence, such as "Removes the old count and the test that only covers it."',
            },
            title: {
              type: 'string',
              minLength: 1,
              maxLength: FINDING_TITLE_MAX_LENGTH,
              // The skill for the kind of check says how to write it, so the launch command, which
              // carries every skill and tool, stays well under its size limit.
              description:
                "The finding's own title, in the findings list, on the fix page and in Slack. The skill for the kind of check says how to write it.",
            },
          },
        },
      },
      summary: {
        type: 'string',
        minLength: 1,
        maxLength: SUMMARY_MAX_LENGTH,
        description:
          "One or two plain sentences the author reads under a headline that names their change and counts the items, in Slack and on this session's page: what the merge left behind and why it matters, saying once what several items have in common. A later call's summary replaces this one.",
      },
      title: {
        type: 'string',
        minLength: 1,
        maxLength: TITLE_MAX_LENGTH,
        description:
          'A few plain words saying what the cleanup is about, for this session\'s title, such as "Remove the old date formatter".',
      },
      not_dead: {
        type: 'array',
        maxItems: MAX_REPORTED_FINDINGS,
        description:
          'Findings you recorded before that someone says are not dead code. Give the recorded path when a name occurs in more than one file.',
        items: {
          type: 'object',
          additionalProperties: false,
          required: ['name', 'status', 'reason'],
          properties: {
            name: {
              type: 'string',
              minLength: 1,
              maxLength: NAME_MAX_LENGTH,
              description: 'The name you recorded.',
            },
            path: {
              type: 'string',
              minLength: 1,
              maxLength: PATH_MAX_LENGTH,
              description: 'Its recorded file, relative to the repository root.',
            },
            status: {
              type: 'string',
              enum: DISMISS_TRIAGE_STATUSES,
              description:
                '`false_positive` when something uses it, `wont_fix` or `intended_behavior` when they keep it on purpose, `accepted_risk` when they accept keeping it.',
            },
            reason: {
              type: 'string',
              minLength: 1,
              maxLength: REASON_MAX_LENGTH,
              description: 'Their reason, in their words where you can.',
            },
          },
        },
      },
    },
  },
  needs: 'liveCall',
} as const satisfies SessionToolDefinition;
